import { useMemo, useState } from 'react';
import { overlapping } from '../lib/format';
import type {
  EnrichedResource,
  NewReservation,
  Reservation,
} from '../lib/types';

/**
 * The top-level booking panel. The conflict list is advisory only —
 * it warns about overlaps but never blocks the save, which is how the
 * original behaved and how the shop actually works (two crews handing
 * a machine over inside one day is a legitimate double-booking).
 */
export function NewReservationForm({
  resources,
  reservations,
  defaultReservedBy,
  saving,
  onSubmit,
  onClose,
}: {
  resources: EnrichedResource[];
  reservations: Reservation[];
  defaultReservedBy: string;
  saving: boolean;
  onSubmit: (reservation: NewReservation) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<NewReservation>({
    equipment: '',
    projectNumber: '',
    projectName: '',
    startDate: '',
    endDate: '',
    reservedBy: defaultReservedBy,
    notes: '',
    status: 'Confirmed',
  });
  const update = <K extends keyof NewReservation>(
    key: K,
    value: NewReservation[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const equipment = useMemo(
    () =>
      resources
        .filter((r) => r.type === 'Equipment')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [resources],
  );
  const personnel = useMemo(
    () =>
      resources
        .filter((r) => r.type === 'Personnel')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [resources],
  );

  const conflicts = useMemo(
    () =>
      overlapping(reservations, form.equipment, form.startDate, form.endDate),
    [reservations, form.equipment, form.startDate, form.endDate],
  );

  const datesValid =
    form.startDate !== '' && form.endDate !== '' && form.endDate >= form.startDate;
  const ready =
    form.equipment !== '' &&
    form.projectNumber.trim() !== '' &&
    form.reservedBy.trim() !== '' &&
    datesValid;

  return (
    <div className="new-res-form">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        <span
          style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}
        >
          New reservation
        </span>
        <button
          style={{
            background: 'none',
            border: 'none',
            fontSize: 18,
            color: 'var(--text-secondary)',
          }}
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <div className="form-grid">
        <div className="form-full">
          <label className="form-label">Resource</label>
          <select
            className="form-input"
            value={form.equipment}
            onChange={(e) => update('equipment', e.target.value)}
          >
            <option value="">Select a resource…</option>
            <optgroup label="── Equipment ──">
              {equipment.map((r) => (
                <option key={r.title} value={r.title}>
                  {r.name} ({r.title})
                </option>
              ))}
            </optgroup>
            <optgroup label="── Personnel ──">
              {personnel.map((r) => (
                <option key={r.title} value={r.title}>
                  {r.name} — {r.category}
                </option>
              ))}
            </optgroup>
          </select>
        </div>

        <div>
          <label className="form-label">Project number</label>
          <input
            className="form-input"
            placeholder="PRJ10xxx"
            value={form.projectNumber}
            onChange={(e) => update('projectNumber', e.target.value)}
          />
        </div>
        <div>
          <label className="form-label">Project name</label>
          <input
            className="form-input"
            placeholder="Project name"
            value={form.projectName}
            onChange={(e) => update('projectName', e.target.value)}
          />
        </div>
        <div>
          <label className="form-label">Start date</label>
          <input
            type="date"
            className="form-input"
            value={form.startDate}
            onChange={(e) => update('startDate', e.target.value)}
          />
        </div>
        <div>
          <label className="form-label">End date</label>
          <input
            type="date"
            className="form-input"
            value={form.endDate}
            onChange={(e) => update('endDate', e.target.value)}
          />
        </div>
        <div>
          <label className="form-label">Reserved by</label>
          <input
            className="form-input"
            placeholder="Name"
            value={form.reservedBy}
            onChange={(e) => update('reservedBy', e.target.value)}
          />
        </div>
        <div>
          <label className="form-label">Status</label>
          <select
            className="form-input"
            value={form.status}
            onChange={(e) =>
              update('status', e.target.value as NewReservation['status'])
            }
          >
            <option>Confirmed</option>
            <option>Pending</option>
            <option>Active</option>
          </select>
        </div>
        <div className="form-full">
          <label className="form-label">Notes</label>
          <input
            className="form-input"
            placeholder="Optional"
            value={form.notes}
            onChange={(e) => update('notes', e.target.value)}
          />
        </div>
      </div>

      {form.startDate !== '' && form.endDate !== '' && !datesValid && (
        <div className="conflict-box">
          <p style={{ fontWeight: 500 }}>End date is before the start date</p>
        </div>
      )}

      {conflicts.length > 0 && (
        <div className="conflict-box">
          <p style={{ fontWeight: 500 }}>Conflict detected</p>
          {conflicts.map((c) => (
            <p key={c.id} style={{ marginTop: 3 }}>
              {c.projectNumber} – {c.projectName} · {c.startDate} → {c.endDate}
            </p>
          ))}
        </div>
      )}

      <div className="form-actions">
        <button
          className="btn-save"
          disabled={saving || !ready}
          onClick={() => onSubmit(form)}
        >
          {saving ? 'Saving…' : 'Save reservation'}
        </button>
        <button className="btn-cancel" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
